import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { Package, DollarSign, Users, TrendingDown, Clock, AlertCircle } from 'lucide-react';
import { apiFetch } from '../utils/apiClient';
import './Dashboard.css';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444'];

const STATUS_STYLES = {
  received: { background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' },
  preparing: { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' },
  ready: { background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' },
  'out for delivery': { background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' },
  delivered: { background: 'rgba(100, 116, 139, 0.1)', color: '#64748b' },
  cancelled: { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' },
};

const StatCard = ({ icon: Icon, label, value, iconBg, iconColor, subLabel, subValue, subColor }) => (
  <div className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
    <div style={{ background: iconBg, color: iconColor, padding: '14px', borderRadius: '12px', flexShrink: 0 }}>
      <Icon size={22} />
    </div>
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '500' }}>{label}</p>
      <h2 style={{ fontSize: '26px', fontWeight: '700', color: 'var(--text-main)', lineHeight: 1 }}>{value}</h2>
      {subLabel && (
        <p style={{ fontSize: '12px', color: subColor || 'var(--text-muted)', marginTop: '6px' }}>
          {subLabel}: <strong>{subValue}</strong>
        </p>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalOrdersToday: 0,
    revenueToday: 0,
    activeEmployees: 0,
    totalAdvances: 0,
    totalSalariesPaid: 0,
    pendingSalaries: 0,
    chartData: [],
    orderBreakdown: [],
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiFetch('/api/dashboard/stats');
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="pulse-indicator" style={{ margin: '0 auto 16px' }}></div>
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-main)', marginBottom: '6px' }}>Dashboard Overview</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
          Welcome back, Admin. Here is today's snapshot — auto-refreshing every 30s.
        </p>
      </div>

      {/* Stat Cards Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '20px', marginBottom: '28px' }}>
        <StatCard
          icon={Package}
          label="Total Orders Today"
          value={stats.totalOrdersToday}
          iconBg="rgba(37, 99, 235, 0.1)"
          iconColor="var(--primary-color)"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue Today"
          value={`Rs${stats.revenueToday}`}
          iconBg="rgba(16, 185, 129, 0.1)"
          iconColor="var(--success-color)"
          subLabel="Salaries Paid"
          subValue={`Rs${stats.totalSalariesPaid}`}
          subColor="var(--success-color)"
        />
        <StatCard
          icon={Users}
          label="Active Employees"
          value={stats.activeEmployees}
          iconBg="rgba(245, 158, 11, 0.1)"
          iconColor="var(--warning-color)"
          subLabel="Pending payroll"
          subValue={stats.pendingSalaries}
          subColor={stats.pendingSalaries > 0 ? 'var(--danger-color)' : 'var(--success-color)'}
        />
        <StatCard
          icon={TrendingDown}
          label="Advances Given"
          value={`Rs${stats.totalAdvances}`}
          iconBg="rgba(239, 68, 68, 0.1)"
          iconColor="var(--danger-color)"
          subLabel="Unpaid salaries"
          subValue={stats.pendingSalaries}
          subColor={stats.pendingSalaries > 0 ? 'var(--danger-color)' : 'var(--text-muted)'}
        />
      </div>

      {/* Charts Row */}
      <div className="dashboard-charts-grid">
        {/* Revenue Line Chart */}
        <div className="card" style={{ height: '340px', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '600' }}>Revenue — Last 7 Days</h3>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 520, height: 300 }}>
              <LineChart data={stats.chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={v => `Rs${v}`} dx={-4} />
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontSize: '13px' }}
                  formatter={v => [`$${v}`, 'Revenue']}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={{ fill: '#2563eb', strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7, strokeWidth: 0, fill: '#1d4ed8' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Breakdown Pie Chart */}

        <div className="card" style={{ height: '340px', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '600' }}>Order Breakdown</h3>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 520, height: 300 }}>
              <PieChart>
                <Pie
                  data={stats.orderBreakdown}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {stats.orderBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontSize: '13px' }}
                />
                <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Recent Orders</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '13px' }}>
            <Clock size={14} />
            Last 5 orders
          </div>
        </div>

        {stats.recentOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>
            <AlertCircle size={32} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
            <p style={{ fontSize: '14px' }}>No orders yet. They'll appear here once customers place them.</p>
          </div>
        ) : (
          <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '600px' }}>
            <thead>
              <tr>
                {['Order ID', 'Customer', 'Type', 'Amount', 'Status', 'Time'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid var(--border-color)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.recentOrders.map((order, i) => {
                const style = STATUS_STYLES[order.status] || {};
                return (
                  <tr key={order.id} style={{ borderBottom: i < stats.recentOrders.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                    <td style={{ padding: '14px 12px', fontWeight: '600', color: 'var(--primary-color)' }}>#{order.id}</td>
                    <td style={{ padding: '14px 12px', fontWeight: '500' }}>{order.customer_name}</td>
                    <td style={{ padding: '14px 12px', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{order.order_type}</td>
                    <td style={{ padding: '14px 12px', fontWeight: '600' }}>Rs.{Number(order.total_amount).toFixed(2)}</td>
                    <td style={{ padding: '14px 12px' }}>
                      <span style={{ ...style, padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' }}>
                        {order.status}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px', color: 'var(--text-muted)' }}>
                      {new Date(order.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
